import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ElegantMarksheet = ({ marksheet, userProfile, onClose }) => {
  const [marksheetTemplate, setMarksheetTemplate] = useState(null);
  
  useEffect(() => {
    if (marksheet?.class) {
      fetchMarksheetTemplate(marksheet.class);
    }
  }, [marksheet?.class]);

  if (!marksheet) return null;

  const fetchMarksheetTemplate = async (className) => {
    try {
      console.log('Fetching template for class:', className);
      const response = await axios.get(`/api/marksheet-template/${className}`);
      console.log('Template fetched successfully:', response.data);
      console.log('Exam structure:', response.data?.examStructure);
      setMarksheetTemplate(response.data);
    } catch (error) {
      console.error('Template fetch failed for class:', className, error);
      console.log('Using fallback template with 80 marks');
      // Fallback to default template
      setMarksheetTemplate({
        subjects: [
          { name: 'hindi', displayName: 'Hindi' },
          { name: 'english', displayName: 'English' },
          { name: 'maths', displayName: 'Mathematics' },
          { name: 'evs', displayName: 'EVS' },
          { name: 'gk', displayName: 'GK' },
          { name: 'computer', displayName: 'Computer' }
        ],
        examStructure: {
          halfYearly: { enabled: true, maxMarks: 80, displayName: 'Half Yearly', order: 1 },
          annualExam: { enabled: true, maxMarks: 80, displayName: 'Annual Exam', order: 2 }
        }
      });
    }
  };

  // Get subjects based on template
  const getSubjectsForClass = () => {
    if (marksheetTemplate?.subjects) {
      return marksheetTemplate.subjects.map(s => s.name);
    }
    // Fallback for classes without templates
    return ['hindi', 'english', 'maths', 'evs', 'gk', 'computer'];
  };

  // Get subject display name from template
  const getSubjectDisplayName = (subject) => {
    const subjectObj = marksheetTemplate?.subjects?.find(s => s.name === subject);
    return subjectObj?.displayName || subject;
  };

  const calculateTotal = (subject) => {
    if (!marksheetTemplate?.examStructure) return 0;
    
    return Object.keys(marksheetTemplate.examStructure)
      .filter(examType => marksheetTemplate.examStructure[examType].enabled && marksheet.visibility?.[examType])
      .reduce((sum, examType) => {
        const marks = marksheet.exams?.[examType]?.[subject];
        return sum + (marks !== undefined && marks !== '' ? parseInt(marks) || 0 : 0);
      }, 0);
  };

  const calculateMaxMarksPerSubject = (subject = null) => {
    if (!marksheetTemplate?.examStructure) return 200; // fallback
    
    return Object.entries(marksheetTemplate.examStructure)
      .filter(([examType, config]) => config.enabled && marksheet.visibility?.[examType])
      .reduce((sum, [examType, config]) => {
        if (subject && (config.subjectSpecificMarks || config.subjectMaxMarks)) {
          const subjectMarks = (config.subjectSpecificMarks || config.subjectMaxMarks)[subject];
          if (subjectMarks) {
            return sum + subjectMarks;
          }
        }
        return sum + config.maxMarks;
      }, 0);
  };

  const calculateGrandTotal = () => {
    const subjects = getSubjectsForClass();
    return subjects.reduce((sum, subject) => sum + calculateTotal(subject), 0);
  };

  const calculatePercentage = () => {
    const grandTotal = calculateGrandTotal();
    const subjects = getSubjectsForClass();
    
    // Calculate total possible marks considering subject-specific marks
    const totalPossible = subjects.reduce((sum, subject) => {
      return sum + calculateMaxMarksPerSubject(subject);
    }, 0);
    
    return totalPossible > 0 ? ((grandTotal / totalPossible) * 100).toFixed(1) : '0.0';
  };

  const getGrade = (percentage) => {
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B+';
    if (percentage >= 60) return 'B';
    if (percentage >= 50) return 'C+';
    if (percentage >= 40) return 'C';
    return 'F';
  };

  const getDivision = (percentage) => {
    if (percentage >= 75) return 'I';
    if (percentage >= 60) return 'II';
    if (percentage >= 40) return 'III';
    return 'Fail';
  };

  const percentage = calculatePercentage();
  const grade = getGrade(percentage);
  const division = getDivision(percentage);

  return (
    <div className="mobile-popup" onClick={onClose}>
      <div className="mobile-popup-content" onClick={(e) => e.stopPropagation()} style={{
        background: 'white',
        borderRadius: '15px',
        border: '3px solid #1e40af'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          color: 'white',
          padding: '20px',
          borderRadius: '12px 12px 0 0',
          textAlign: 'center',
          position: 'relative'
        }}>

          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '15px' }}>
            <div style={{
              width: '60px',
              height: '60px',
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '30px',
              border: '2px solid rgba(255,255,255,0.3)'
            }}>
              <img 
                src="/logo.png" 
                alt="School Logo" 
                style={{
                  width: '45px',
                  height: '45px',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '🏫';
                }}
              />
            </div>
            <div>
              <h1 style={{ 
                margin: '0', 
                fontSize: '28px', 
                fontWeight: '700',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}>
                {localStorage.getItem('schoolName') || 'The Rising School'}
              </h1>
              <p style={{ 
                margin: '5px 0 0 0', 
                fontSize: '14px', 
                opacity: '0.9'
              }}>
                {localStorage.getItem('schoolAddress') || 'Savina, Udaipur'} | 📞 {localStorage.getItem('schoolPhone') || '8209683549'}
              </p>
            </div>
          </div>
          
          <div style={{
            background: 'rgba(255,255,255,0.15)',
            padding: '8px 20px',
            borderRadius: '20px',
            display: 'inline-block',
            fontSize: '16px',
            fontWeight: '600',
            border: '1px solid rgba(255,255,255,0.3)'
          }}>
            📊 ACADEMIC PROGRESS REPORT
          </div>
        </div>
        
        <button 
          className="mobile-popup-close"
          onClick={onClose}
        >
          ×
        </button>

        {/* Student Info */}
        <div style={{
          padding: '25px',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderBottom: '2px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '12px',
              background: userProfile?.profilePicture 
                ? `url(${userProfile.profilePicture}) center/cover no-repeat` 
                : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '35px',
              border: '3px solid #1e40af',
              boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)',
              overflow: 'hidden'
            }}>
              {!userProfile?.profilePicture && '👤'}
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={{ 
                margin: '0 0 8px 0', 
                fontSize: '24px', 
                fontWeight: '700',
                color: '#1e293b'
              }}>
                {marksheet.name}
              </h2>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                  color: 'white',
                  padding: '6px 15px',
                  borderRadius: '15px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  Class {marksheet.class}
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  padding: '6px 15px',
                  borderRadius: '15px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  Session: {marksheet.academicYear || '2026-2027'}
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  padding: '6px 15px',
                  borderRadius: '15px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  Teacher: {marksheet.classTeacher}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Marks Table */}
        <div style={{ padding: '25px' }}>
          <h3 style={{
            margin: '0 0 20px 0',
            fontSize: '20px',
            fontWeight: '700',
            color: '#1e293b',
            textAlign: 'center',
            padding: '10px',
            background: 'linear-gradient(135deg, #e0f2fe 0%, #b3e5fc 100%)',
            borderRadius: '10px',
            border: '2px solid #0ea5e9'
          }}>
            📊 Subject-wise Performance
          </h3>

          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <thead>
                <tr style={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                  color: 'white'
                }}>
                  <th style={{ padding: '15px 10px', textAlign: 'center', fontWeight: '700' }}>Subject</th>
                  {marksheetTemplate?.examStructure && Object.entries(marksheetTemplate.examStructure)
                    .filter(([examType, config]) => config.enabled && marksheet.visibility?.[examType])
                    .sort(([,a], [,b]) => (a.order || 0) - (b.order || 0))
                    .map(([examType, config]) => {
                      // Check if this exam has subject-specific marks
                      const hasSubjectSpecificMarks = (config.subjectSpecificMarks || config.subjectMaxMarks) && Object.keys(config.subjectSpecificMarks || config.subjectMaxMarks || {}).length > 0;
                      
                      if (hasSubjectSpecificMarks) {
                        // For subject-specific marks, show the range or 'Varies'
                        const marks = Object.values(config.subjectSpecificMarks || config.subjectMaxMarks || {});
                        const minMarks = Math.min(...marks);
                        const maxMarks = Math.max(...marks);
                        const displayMarks = minMarks === maxMarks ? maxMarks : `${minMarks}-${maxMarks}`;
                        
                        return (
                          <th key={examType} style={{ padding: '15px 10px', textAlign: 'center', fontWeight: '700' }}>
                            {config.displayName || examType.replace(/([A-Z])/g, ' $1')}<br/>({displayMarks})
                          </th>
                        );
                      } else {
                        // For uniform marks, show the single value
                        return (
                          <th key={examType} style={{ padding: '15px 10px', textAlign: 'center', fontWeight: '700' }}>
                            {config.displayName || examType.replace(/([A-Z])/g, ' $1')}<br/>({config.maxMarks})
                          </th>
                        );
                      }
                    })
                  }
                  <th style={{ padding: '15px 10px', textAlign: 'center', fontWeight: '700' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {getSubjectsForClass().map((subject, index) => {
                  const total = calculateTotal(subject);
                  const maxMarksForThisSubject = calculateMaxMarksPerSubject(subject);
                  const subjectPercentage = maxMarksForThisSubject > 0 ? ((total / maxMarksForThisSubject) * 100).toFixed(1) : '0.0';
                  const rowBg = index % 2 === 0 ? '#f8fafc' : '#ffffff';
                  
                  return (
                    <tr key={subject} style={{ background: rowBg }}>
                      <td style={{
                        padding: '12px 10px',
                        fontWeight: '700',
                        textTransform: 'capitalize',
                        color: '#1e293b',
                        borderBottom: '1px solid #e2e8f0'
                      }}>
                        {getSubjectDisplayName(subject)}
                      </td>
                      {marksheetTemplate?.examStructure && Object.keys(marksheetTemplate.examStructure)
                        .filter(examType => marksheetTemplate.examStructure[examType].enabled && marksheet.visibility?.[examType])
                        .map(examType => {
                          const config = marksheetTemplate.examStructure[examType];
                          const hasSubjectSpecificMarks = (config.subjectSpecificMarks || config.subjectMaxMarks) && Object.keys(config.subjectSpecificMarks || config.subjectMaxMarks || {}).length > 0;
                          const subjectMaxMarks = hasSubjectSpecificMarks ? (config.subjectSpecificMarks || config.subjectMaxMarks)[subject] || config.maxMarks : config.maxMarks;
                          const obtainedMarks = marksheet.exams?.[examType]?.[subject];
                          const displayMarks = obtainedMarks !== undefined && obtainedMarks !== '' ? obtainedMarks : '-';
                          
                          return (
                            <td key={examType} style={{ 
                              padding: '12px 10px', 
                              textAlign: 'center', 
                              borderBottom: '1px solid #e2e8f0', 
                              fontWeight: '600'
                            }}>
                              {displayMarks}
                              {hasSubjectSpecificMarks && displayMarks !== '-' && (
                                <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>/{subjectMaxMarks}</div>
                              )}
                            </td>
                          );
                        })
                      }
                      <td style={{
                        padding: '12px 10px',
                        textAlign: 'center',
                        fontWeight: '800',
                        fontSize: '16px',
                        color: '#1e293b',
                        borderBottom: '1px solid #e2e8f0',
                        background: '#f0fdf4'
                      }}>
                        {total}
                      </td>
                    </tr>
                  );
                })}
              </tbody>

            </table>
          </div>

          {/* Term Percentages - Only show enabled terms */}
          <div style={{
            marginTop: '25px',
            padding: '20px',
            background: 'linear-gradient(135deg, #f0f8ff 0%, #e0f2fe 100%)',
            borderRadius: '12px',
            border: '2px solid #0ea5e9'
          }}>
            <h4 style={{
              margin: '0 0 15px 0',
              color: '#0c4a6e',
              textAlign: 'center',
              fontSize: '18px',
              fontWeight: '700'
            }}>📊 Term Performance</h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '12px'
            }}>
              {marksheetTemplate?.examStructure && Object.entries(marksheetTemplate.examStructure)
                .filter(([examType, config]) => config.enabled && marksheet.visibility?.[examType])
                .sort(([,a], [,b]) => (a.order || 0) - (b.order || 0))
                .map(([examType, config]) => {
                const allSubjects = getSubjectsForClass();
                const totalObtained = allSubjects.reduce((sum, subject) => 
                  sum + (parseInt(marksheet.exams?.[examType]?.[subject]) || 0), 0);
                
                // Calculate total possible marks for this exam considering subject-specific marks
                const totalPossible = allSubjects.reduce((sum, subject) => {
                  const subjectMarks = (config.subjectSpecificMarks || config.subjectMaxMarks);
                  if (subjectMarks && subjectMarks[subject]) {
                    return sum + subjectMarks[subject];
                  }
                  return sum + config.maxMarks;
                }, 0);
                
                const percentage = totalPossible > 0 ? ((totalObtained / totalPossible) * 100).toFixed(1) : '0.0';
                
                return (
                  <div key={examType} style={{
                    padding: '15px',
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    borderRadius: '10px',
                    textAlign: 'center',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                  }}>
                    <div style={{
                      fontWeight: '600',
                      fontSize: '12px',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      opacity: '0.9'
                    }}>
                      {config.displayName || examType.replace(/([A-Z])/g, ' $1')}
                    </div>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: '800'
                    }}>{percentage}%</div>
                    <div style={{
                      fontSize: '10px',
                      opacity: '0.8',
                      marginTop: '4px'
                    }}>
                      ({totalObtained}/{totalPossible})
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Maximum Marks Information */}
          <div style={{
            marginTop: '20px',
            padding: '15px',
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            borderRadius: '10px',
            border: '2px solid #f59e0b',
            textAlign: 'center'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#92400e', fontSize: '16px', fontWeight: '700' }}>
              📊 Marking Scheme
            </h4>
            <div style={{ fontSize: '14px', color: '#92400e' }}>
              <strong>Maximum Marks per Subject:</strong> Varies by subject and exam
              <br />
              <strong>Total Maximum Marks:</strong> {getSubjectsForClass().reduce((sum, subject) => sum + calculateMaxMarksPerSubject(subject), 0)} marks
            </div>
          </div>

          {/* Result Summary */}
          {marksheet.visibility?.finalResult && (
            <div style={{
              marginTop: '25px',
              padding: '25px',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              borderRadius: '15px',
              color: 'white',
              textAlign: 'center',
              border: '3px solid #334155'
            }}>
              <h3 style={{
                margin: '0 0 20px 0',
                fontSize: '24px',
                fontWeight: '700'
              }}>
                🏆 FINAL RESULT
              </h3>
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '30px',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  background: 'rgba(255,255,255,0.1)',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '2px solid rgba(255,255,255,0.2)',
                  minWidth: '150px'
                }}>
                  <div style={{ fontSize: '14px', opacity: '0.9', marginBottom: '8px' }}>Percentage</div>
                  <div style={{ fontSize: '32px', fontWeight: '800' }}>{percentage}%</div>
                </div>
                <div style={{
                  background: 'rgba(255,255,255,0.1)',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '2px solid rgba(255,255,255,0.2)',
                  minWidth: '150px'
                }}>
                  <div style={{ fontSize: '14px', opacity: '0.9', marginBottom: '8px' }}>Grade</div>
                  <div style={{ fontSize: '32px', fontWeight: '800' }}>{grade}</div>
                </div>
                <div style={{
                  background: 'rgba(255,255,255,0.1)',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '2px solid rgba(255,255,255,0.2)',
                  minWidth: '150px'
                }}>
                  <div style={{ fontSize: '14px', opacity: '0.9', marginBottom: '8px' }}>Division</div>
                  <div style={{ fontSize: '32px', fontWeight: '800' }}>{division}</div>
                </div>
              </div>
            </div>
          )}

          {/* Additional Information */}
          <div style={{
            marginTop: '25px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px'
          }}>
            {/* Co-Curricular Activities */}
            {marksheetTemplate?.additionalFields?.some(f => f.category === 'coCurricular') && (
              <div style={{
                padding: '20px',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderRadius: '12px',
                border: '2px solid #f59e0b'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#92400e', fontSize: '16px', fontWeight: '700' }}>
                  🎨 Co-Curricular Activities
                </h4>
                <div style={{ fontSize: '14px', color: '#92400e' }}>
                  {marksheetTemplate.additionalFields
                    .filter(field => field.category === 'coCurricular')
                    .map(field => {
                      let value = 'N/A';
                      ['annualExam', 'halfYearly', 'term3', 'term2', 'term1'].forEach(examType => {
                        if (marksheet.visibility?.[examType] && marksheet.exams?.[examType]?.[field.name]) {
                          value = marksheet.exams[examType][field.name];
                        }
                      });
                      
                      return (
                        <p key={field.name} style={{ margin: '5px 0' }}>
                          <strong>{field.displayName}:</strong> {value || 'N/A'}
                        </p>
                      );
                    })
                  }
                </div>
              </div>
            )}

            {/* Attendance & PTM */}
            {marksheetTemplate?.additionalFields?.some(f => f.category === 'attendance') && (
              <div style={{
                padding: '20px',
                background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                borderRadius: '12px',
                border: '2px solid #10b981'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#065f46', fontSize: '16px', fontWeight: '700' }}>
                  📅 Attendance & PTM
                </h4>
                <div style={{ fontSize: '14px', color: '#065f46' }}>
                  {marksheetTemplate.additionalFields
                    .filter(field => field.category === 'attendance')
                    .map(field => {
                      let value = 'N/A';
                      ['annualExam', 'halfYearly', 'term3', 'term2', 'term1'].forEach(examType => {
                        if (marksheet.visibility?.[examType] && marksheet.exams?.[examType]?.[field.name]) {
                          value = marksheet.exams[examType][field.name];
                        }
                      });
                      
                      return (
                        <p key={field.name} style={{ margin: '5px 0' }}>
                          <strong>{field.displayName}:</strong> {value || 'N/A'}
                        </p>
                      );
                    })
                  }
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            marginTop: '30px',
            padding: '20px',
            background: 'linear-gradient(135deg, #e0f2fe 0%, #b3e5fc 100%)',
            borderRadius: '12px',
            textAlign: 'center',
            border: '2px solid #0ea5e9'
          }}>
            <p style={{
              margin: '0',
              fontSize: '16px',
              fontWeight: '600',
              color: '#0c4a6e'
            }}>
              🌟 {localStorage.getItem('schoolName') || 'The Rising School'} wishes you all the best for your future endeavors! 🌟
            </p>
            <p style={{
              margin: '10px 0 0 0',
              fontSize: '12px',
              color: '#0369a1'
            }}>
              Generated on {new Date().toLocaleDateString()} | Academic Session {marksheet.academicYear || '2026-2027'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ElegantMarksheet;